import type {LoaderFunctionArgs, MetaFunction} from 'react-router'
import {redirect, useLoaderData} from 'react-router'
import {mergeMeta} from '@core/lib/mergeMeta'
import {displayCurrency} from '@core/billing/displayCurrency'
import {logger} from '@core/utils/logger.server'
import {BASE_URL, getHreflangMeta, homeLang, HREFLANG_MAP, retiredHomeTarget} from '@product/quies/lib/hreflang'
import type {Lang} from '@product/quies/lib/hreflang'
import {HOME_CONTENT} from '@product/quies/data/home-content'
import {HomePage} from '@product/quies/components/HomePage/HomePage'
import {loadPricings} from '@product/quies/services/pricing.server'

const homeUrl = (lang: Lang) => `${BASE_URL}${HREFLANG_MAP.home[lang]}`

export async function loader({params, request}: LoaderFunctionArgs) {
    const retiredTo = retiredHomeTarget(params.lang)
    if (retiredTo) {
        throw redirect(`${retiredTo}${new URL(request.url).search}`, 301)
    }

    // The optional segment matches every single-segment path no static route claims.
    const lang = homeLang(params.lang)
    if (!lang) {
        throw new Response('Not Found', {status: 404})
    }

    // The page outlives a price it cannot read: it renders with no cards, and the failure is logged.
    const pricings = await loadPricings(displayCurrency(request)).catch((error: unknown) => {
        logger.error('[Home] pricings could not be loaded', {error})
        return []
    })

    return {lang, pricings}
}

export const meta: MetaFunction<typeof loader> = ({data, matches}) => {
    if (!data) return mergeMeta(matches, [])
    const content = HOME_CONTENT[data.lang]
    const canonicalUrl = homeUrl(data.lang)

    return mergeMeta(matches, [
        {title: content.meta.title},
        {name: 'description', content: content.meta.description},
        {tagName: 'link', rel: 'canonical', href: canonicalUrl},
        ...getHreflangMeta('home'),
        {property: 'og:title', content: content.meta.ogTitle},
        {property: 'og:description', content: content.meta.ogDescription},
        {property: 'og:url', content: canonicalUrl},
    ])
}

export default function Home() {
    const {lang, pricings} = useLoaderData<typeof loader>()
    return <HomePage content={HOME_CONTENT[lang]} canonicalUrl={homeUrl(lang)} pricings={pricings}/>
}
